/**
 * Client-side mirror of the backend's `RequestFieldSchema` value rules
 * (`backend/WebApi/src/Services/Teleconsultation/RequestFieldSchema.cs`). The
 * server stays the authority — this only stops a submit that would 400, and
 * formats stored answers for the radiologist's read-only view.
 */
/** Matches RequestFieldSchema.MaxValueLength. */
export const MAX_REQUEST_VALUE_LENGTH = 2000;
/** Sentinel option value for "clear this select" (Radix Select forbids an empty value). */
export const REQUEST_SELECT_NONE = '__none__';
/**
 * The two standard questions are ordinary request fields with RESERVED keys
 * (mirrors RequestFieldSchema.ClinicalHistoryKey / SpecificQuestionsKey). Their
 * answers travel in the case's own `clinicalHistory` / `specificQuestions`
 * payload members and land in the columns every radiologist-side surface reads,
 * never inside `requestValues`. A template may keep, rename, reorder, or drop
 * them like any other question.
 */
export const CLINICAL_HISTORY_KEY = 'clinicalHistory';
export const SPECIFIC_QUESTIONS_KEY = 'specificQuestions';
export function isReservedRequestKey(key) {
    return key === CLINICAL_HISTORY_KEY || key === SPECIFIC_QUESTIONS_KEY;
}
/**
 * The standard questions as stored wire text (English). This is what a new
 * teleconsultation template starts from in the editor and what the migration
 * spliced into pre-existing custom forms; the submit dialog renders the
 * localized twin from {@link standardRequestFields}.
 */
export const STANDARD_REQUEST_FIELDS_EN = [
    {
        key: CLINICAL_HISTORY_KEY,
        label: 'Clinical History',
        type: 'textarea',
        required: true,
        placeholder: 'Describe clinical presentation, symptoms, and reason for consultation...',
        helpText: 'Include relevant history, duration of symptoms, medications, and prior treatments.',
    },
    {
        key: SPECIFIC_QUESTIONS_KEY,
        label: 'Specific Questions for Radiologist',
        type: 'textarea',
        required: false,
        placeholder: 'e.g. Please evaluate for evidence of metastasis. Is there a foreign body?',
    },
];
/** The standard request form, localized: what a submitter answers by default. */
export function standardRequestFields(t) {
    return [
        {
            key: CLINICAL_HISTORY_KEY,
            label: t('submit.clinicalHistory'),
            type: 'textarea',
            required: true,
            placeholder: t('submit.clinicalHistoryPlaceholder'),
            helpText: t('submit.clinicalHistoryHelp'),
        },
        {
            key: SPECIFIC_QUESTIONS_KEY,
            label: t('submit.specificQuestions'),
            type: 'textarea',
            required: false,
            placeholder: t('submit.specificQuestionsPlaceholder'),
        },
    ];
}
/**
 * The form a submitter answers: the picked consultation type's own fields when
 * it defines any, otherwise the standard form (no type picked, or a template
 * with no request fields). A template's fields REPLACE the standard form —
 * nothing fixed is rendered around them. Mirrors RequestFieldSchema.EffectiveForm.
 */
export function effectiveRequestFields(templateFields, t) {
    return templateFields && templateFields.length > 0 ? templateFields : standardRequestFields(t);
}
/**
 * Splits a form's answers into what the submit payload carries: the reserved
 * narratives as the text the case columns store, and the remaining answers
 * pruned for `requestValues`. A reserved question the form does not ask (or
 * left blank) yields ''.
 */
export function splitRequestValues(fields, values, options = {}) {
    const narrative = (key) => {
        const field = fields.find((candidate) => candidate.key === key);
        if (!field)
            return '';
        const value = values[field.key];
        if (isBlankRequestValue(value))
            return '';
        return typeof value === 'string' ? value.trim() : formatRequestValue(field, value, options);
    };
    return {
        clinicalHistory: narrative(CLINICAL_HISTORY_KEY),
        specificQuestions: narrative(SPECIFIC_QUESTIONS_KEY),
        requestValues: pruneRequestValues(fields.filter((field) => !isReservedRequestKey(field.key)), values),
    };
}
const ERROR_MESSAGE_KEY = {
    required: 'submit.fieldErrorRequired',
    notNumber: 'submit.fieldErrorNumber',
    min: 'submit.fieldErrorMin',
    max: 'submit.fieldErrorMax',
    notDate: 'submit.fieldErrorDate',
    notOption: 'submit.fieldErrorOption',
    tooLong: 'submit.fieldErrorTooLong',
};
/** Field key -> localized message, first violation per field wins. */
export function localizeRequestFieldErrors(errors, t) {
    const messages = {};
    for (const error of errors) {
        if (error.key in messages)
            continue;
        messages[error.key] = t(ERROR_MESSAGE_KEY[error.code], { limit: error.params?.limit });
    }
    return messages;
}
/** DOM id for a field's control — also the anchor the dialog focuses on error. */
export function requestFieldInputId(key) {
    return `request-field-${key}`;
}
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** True when the answer carries nothing (absent, blank string, or empty array). */
export function isBlankRequestValue(value) {
    if (value === null || value === undefined)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
function isValidDate(text) {
    if (!ISO_DATE.test(text))
        return false;
    const [year, month, day] = text.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day);
}
function toNumber(value) {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim();
    if (trimmed === '')
        return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
}
/**
 * Every rule the backend would reject the submission for, in field order.
 * A blank answer to an optional field is valid and is simply dropped.
 */
export function validateRequestValues(fields, values) {
    const errors = [];
    for (const field of fields) {
        const value = values[field.key];
        if (isBlankRequestValue(value)) {
            if (field.required)
                errors.push({ key: field.key, code: 'required' });
            continue;
        }
        switch (field.type) {
            case 'number': {
                const parsed = toNumber(value);
                if (parsed === null) {
                    errors.push({ key: field.key, code: 'notNumber' });
                }
                else if (field.min != null && parsed < field.min) {
                    errors.push({ key: field.key, code: 'min', params: { limit: field.min } });
                }
                else if (field.max != null && parsed > field.max) {
                    errors.push({ key: field.key, code: 'max', params: { limit: field.max } });
                }
                break;
            }
            case 'date': {
                if (typeof value !== 'string' || !isValidDate(value.trim())) {
                    errors.push({ key: field.key, code: 'notDate' });
                }
                break;
            }
            case 'select': {
                // react-doctor-disable-next-line react-doctor/js-set-map-lookups -- one check per select field, against that field's own few options
                if (typeof value !== 'string' || !(field.options ?? []).includes(value)) {
                    errors.push({ key: field.key, code: 'notOption' });
                }
                break;
            }
            case 'multiselect': {
                const options = field.options ?? [];
                // react-doctor-disable-next-line react-doctor/js-set-map-lookups -- a field's few selected values against its few options
                if (!Array.isArray(value) || value.some((entry) => !options.includes(entry))) {
                    errors.push({ key: field.key, code: 'notOption' });
                }
                break;
            }
            case 'boolean':
                break;
            default: {
                // The reserved narratives live in unbounded text columns, so the
                // request_values ceiling does not apply to them.
                if (typeof value !== 'string') {
                    errors.push({ key: field.key, code: 'notOption' });
                }
                else if (!isReservedRequestKey(field.key) && value.length > MAX_REQUEST_VALUE_LENGTH) {
                    errors.push({
                        key: field.key,
                        code: 'tooLong',
                        params: { limit: MAX_REQUEST_VALUE_LENGTH },
                    });
                }
                break;
            }
        }
    }
    return errors;
}
/**
 * The answers to actually submit: only keys the template defines, blanks
 * dropped, numbers sent as numbers and text trimmed. Mirrors what the server
 * would normalize to, so a round-trip read shows what was sent.
 */
export function pruneRequestValues(fields, values) {
    const pruned = {};
    for (const field of fields) {
        const value = values[field.key];
        if (isBlankRequestValue(value))
            continue;
        if (field.type === 'number') {
            const parsed = toNumber(value);
            if (parsed !== null)
                pruned[field.key] = parsed;
            continue;
        }
        if (field.type === 'boolean') {
            pruned[field.key] = value === true || value === 'true';
            continue;
        }
        if (field.type === 'multiselect') {
            pruned[field.key] = Array.isArray(value) ? [...value] : [];
            continue;
        }
        pruned[field.key] = typeof value === 'string' ? value.trim() : value;
    }
    return pruned;
}
/** text and textarea both hold a string, so an answer moves freely between them. */
function sameAnswerShape(a, b) {
    if (a === b)
        return true;
    const isText = (type) => type === 'text' || type === 'textarea';
    return isText(a) && isText(b);
}
/**
 * Carries answers across a consultation-type switch: an answer survives only
 * when the newly-picked form still defines that key with the SAME answer shape
 * (a "Murmur grade" select answer must not leak into a number field). Because
 * the standard questions are fields like any other, a history typed before
 * picking a type follows the submitter into a form that still asks for it.
 */
export function preserveRequestValues(values, previousFields, nextFields) {
    const previousTypes = new Map(previousFields.map((field) => [field.key, field.type]));
    const preserved = {};
    for (const field of nextFields) {
        if (!(field.key in values))
            continue;
        if (!previousTypes.has(field.key))
            continue;
        if (!sameAnswerShape(previousTypes.get(field.key), field.type))
            continue;
        preserved[field.key] = values[field.key];
    }
    return preserved;
}
/** Display string for an answer whose field definition is known. */
export function formatRequestValue(field, value, options = {}) {
    if (field.type === 'boolean') {
        const truthy = value === true || value === 'true';
        return truthy ? (options.yes ?? 'Yes') : (options.no ?? 'No');
    }
    if (field.type === 'multiselect') {
        return Array.isArray(value) ? value.join(', ') : String(value);
    }
    if (field.type === 'number' && field.unit) {
        return `${String(value)} ${field.unit}`;
    }
    return formatRawRequestValue(value, options);
}
/** Display string for an answer whose template has since been deleted. */
function formatRawRequestValue(value, options = {}) {
    if (typeof value === 'boolean')
        return value ? (options.yes ?? 'Yes') : (options.no ?? 'No');
    if (Array.isArray(value))
        return value.join(', ');
    return String(value);
}
/**
 * The answers a case's request form should display, in template order when the
 * definitions survive, otherwise as raw key/value pairs (deleted template).
 * Blank answers are omitted either way.
 */
export function buildRequestAnswers(fields, values, options = {}) {
    if (!values)
        return [];
    if (fields && fields.length > 0) {
        return fields.flatMap((field) => {
            const value = values[field.key];
            if (isBlankRequestValue(value))
                return [];
            return [
                { key: field.key, label: field.label, value: formatRequestValue(field, value, options) },
            ];
        });
    }
    return Object.entries(values).flatMap(([key, value]) => {
        if (isBlankRequestValue(value))
            return [];
        return [{ key, label: key, value: formatRawRequestValue(value, options) }];
    });
}
